/**
 * 에이전트 설치와 배포.
 *
 * 이 화면은 성격이 다른 두 가지를 다룬다.
 *
 * **처음 설치** — 현장에 나가는 사람이 화면 앞에서 할 일을 두 줄로 줄인다:
 * 한 줄 설치 명령을 붙여 넣고, 화면에 뜬 6자를 콘솔에 적는다. 그래서 이 부분의
 * 주인공은 복사 버튼이다.
 *
 * **업데이트 배포(롤아웃)** — 이미 도는 화면들에 새 버전을 내보낸다. 산출물을
 * **올리는 것만으로는 아무 화면도 받지 않는다.** 채널에 버전을 지정해야 나간다.
 * 이 둘을 한 덩어리로 보이게 하면 "올렸는데 왜 안 나가지"가 반복되므로 판을
 * 나누고, 롤아웃 쪽에는 **영향 범위(대상 화면 수)를 먼저 보여준 뒤** 누르게 했다.
 * 되돌릴 수 없는 조작이기 때문이다(업데이터는 다운그레이드를 거부한다).
 *
 * **산출물을 손으로 내려받는 판은 두지 않는다.** 설치 경로가 둘이면 현장은 더 쉬워
 * 보이는 쪽(파일을 받아 실행)을 고르는데, 그 길에는 설치 토큰도 서버 주소도 실리지
 * 않아 결국 화면 앞에서 주소를 손으로 넣게 된다. 설치는 위의 한 줄 설치 카드 하나로
 * 간다. 산출물 등록은 배포의 재료라 남기되, 쓰는 자리인 롤아웃 판에 붙였다.
 */
import { useState, type ReactNode } from 'react';
import {
  Apple,
  Check,
  Clock,
  Copy,
  FlaskConical,
  Monitor,
  Rocket,
  Smartphone,
  Terminal,
  Trash2,
  Upload,
} from 'lucide-react';
import type { AgentRelease, InstallWindow, Me, RolloutState, SignageOneClient } from '../api/client.js';
import {
  Badge,
  CommandBox,
  Empty,
  ErrorNote,
  formatBytes,
  Modal,
  Panel,
  relativeTime,
  RelTime,
} from '../components/ui.js';
import { useAction, useAsync } from '../hooks.js';
import { can } from '../capabilities.js';
import { compareVersions, guessKind, releaseVersions, type VersionOption } from '../releases.js';
import { QrCode } from '../components/QrCode.js';
import { HelpTip } from '../components/HelpTip.js';
import { Disclosure } from '../components/Disclosure.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';

type Platform = 'unix' | 'windows' | 'android';

export function AgentInstallScreen({
  client,
  me,
}: {
  client: SignageOneClient;
  me: Me | null;
}): ReactNode {
  const catalog = useAsync(() => client.agentCatalog(), [client]);
  /* 권한이 없으면 묻지도 않는다 — 어차피 403 이고, 그 오류를 화면에 띄울 자리도 없다. */
  const tokens = useAsync(
    () => (can(me, 'fleet:write') ? client.listInstallTokens() : Promise.resolve(null)),
    [client, me],
  );
  const action = useAction();

  const [platform, setPlatform] = useState<Platform>('unix');
  const [issued, setIssued] = useState<{ token: string; code: string; expires_at: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  /** 방금 올린 버전. "등록 ≠ 배포"를 그 자리에서 말해 주기 위해 기억한다. */
  const [uploaded, setUploaded] = useState<string | null>(null);

  const isAdmin = can(me, 'system');
  /** 설치 토큰을 발급·조회할 수 있는가(`fleet:write`). 없으면 그 카드는 그리지 않는다. */
  const canToken = can(me, 'fleet:write');
  const baseUrl = catalog.data?.install.base_url ?? '';
  const releases = catalog.data?.items ?? [];

  /*
   * 채널별 화면 수는 `admin` 에게만 온다(`/admin/rollout`). 그 아래 권한도 이
   * 화면에 들어오므로(`fleet:write`), 버전만이라도 카탈로그에서 읽어 보여준다.
   */
  const rollout = useAsync(
    () => (isAdmin ? client.rollout() : Promise.resolve(null)),
    [client, isAdmin],
  );
  const channels = rollout.data
    ? { stable: rollout.data.stable_version, canary: rollout.data.canary_version }
    : {
        stable: catalog.data?.rollout.stable_version ?? null,
        canary: catalog.data?.rollout.canary_version ?? null,
      };

  /*
   * 토큰을 넣지 않은 명령도 그대로 쓸 수 있다 — 스크립트가 /dev/tty 로 물어본다.
   * 다만 붙여 넣기 한 번으로 끝나는 편이 현장에서 훨씬 낫다.
   */
  /**
   * 설치 토큰 발급.
   *
   * 한 곳에 모은 이유가 있다 — 이 동작은 화면 두 자리에서 불린다. 아래 버튼과
   * **QR 자리** 다. 토큰이 없으면 QR 을 그릴 수 없는데, 고치는 버튼이 QR 에서 멀리
   * 떨어져 있으면 사람은 "QR 이 안 나온다"로 읽고 만다. 실제로 그렇게 헤맸다.
   */
  const issueToken = (): void => {
    void action.run(
      () => client.createInstallToken('콘솔에서 발급'),
      (result) => {
        setIssued(result);
        tokens.reload();
      },
    );
  };

  const token = issued?.token ?? '<설치토큰>';
  const androidReleases = releases.filter((release) => release.target === 'android');
  const command =
    platform === 'unix'
      ? `curl -fsSL ${baseUrl}/install.sh | sh -s -- --token ${token}`
      : platform === 'windows'
        ? `$env:SIGNAGEONE_INSTALL_TOKEN="${token}"; irm ${baseUrl}/install.ps1 | iex`
        : '';

  return (
    <div className="screen animate-fade-in">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {catalog.error ? <ErrorNote message={catalog.error} /> : null}

      {/* ── 한 줄 설치 ─────────────────────────────────────────── */}
      <Panel
        title="한 줄 설치"
        help="설치한 에이전트는 이 서버 주소를 기억해 자동으로 연결됩니다."
        actions={
          <div className="seg">
            <TabButton active={platform === 'unix'} onClick={() => setPlatform('unix')} icon={Apple}>
              macOS · Linux
            </TabButton>
            <TabButton active={platform === 'windows'} onClick={() => setPlatform('windows')} icon={Monitor}>
              Windows
            </TabButton>
            <TabButton active={platform === 'android'} onClick={() => setPlatform('android')} icon={Smartphone}>
              Android
            </TabButton>
          </div>
        }
      >
        <div className="p-4">
          {/* 안드로이드는 토큰을 쓰지 않는다 — 주소 하나로 설명 화면까지 간다. */}
          {platform === 'android' ? (
            <AndroidInstall baseUrl={baseUrl} releases={androidReleases} />
          ) : (
            <>
              <p className="hint mb-3">
                {platform === 'windows'
                  ? '사이니지 PC 의 PowerShell 에 붙여 넣으세요.'
                  : '사이니지 PC 의 터미널에 붙여 넣으세요.'}
              </p>

              <CommandBox command={command} />
            </>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={action.pending}
              onClick={issueToken}
            >
              <Terminal className="h-3.5 w-3.5" strokeWidth={2} />
              {issued ? '설치 토큰 재발급' : '설치 토큰 발급'}
            </button>
            {issued ? (
              <span className="hint">
                <RelTime iso={issued.expires_at} /> 만료 · 지금 이 화면에서만 볼 수 있습니다
              </span>
            ) : (
              <HelpTip text="발급한 토큰이 위 명령에 채워집니다." label="설치 토큰 발급 도움말" />
            )}
          </div>

          <ol className="mt-4 flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-[13.5px] text-slate-600">
            <li>
              <strong className="text-slate-800">1.</strong>{' '}
              {platform === 'android'
                ? '위 방법 중 하나로 기기에 APK 를 설치합니다.'
                : '위 명령을 사이니지 PC 에서 실행합니다.'}
            </li>
            <li>
              <strong className="text-slate-800">2.</strong> 에이전트를 실행하면 화면에{' '}
              <strong className="text-slate-800">6자 페어링 코드</strong>가 표시됩니다.
            </li>
            <li>
              <strong className="text-slate-800">3.</strong> 콘솔 ▸ 화면 ▸ <strong className="text-slate-800">화면 추가</strong>{' '}
              에 그 코드를 입력합니다.
            </li>
          </ol>
        </div>
      </Panel>

      {/*
        ── 롤아웃 ───────────────────────────────────────────────

        **관리자에게만 그린다.** 이 카드가 하는 일(채널에 버전 지정·산출물 등록)은 전부
        `admin` 이고, 채널별 화면 수도 `admin` 에게만 온다. 권한이 없는 사람에게는 고를 수
        없는 select 와 숫자 없는 표만 남아, 화면이 할 수 없는 일로 채워진다.
      */}
      {isAdmin ? (
      <RolloutPanel
        client={client}
        releases={releases}
        state={rollout.data}
        error={rollout.error}
        stableVersion={channels.stable}
        canaryVersion={channels.canary}
        uploaded={uploaded}
        onUpload={() => setUploading(true)}
        onChanged={() => {
          rollout.reload();
          catalog.reload();
          setUploaded(null);
        }}
      />
      ) : null}

      {/*
        설치 토큰은 대부분 한 번 발급하고 다시 보지 않는다. 늘 펼쳐 두면 이 화면의
        주인공(설치 명령)보다 표가 더 큰 자리를 차지한다.
      */}
      {canToken ? (
      <details className="panel group">
        <summary className="panel-head cursor-pointer list-none">
          <h2>
            설치 토큰
            <HelpTip
              text="에이전트를 내려받을 때 쓰는 토큰입니다. 여러 대에 재사용할 수 있고, 만료되면 자동으로 사라집니다."
              label="설치 토큰 도움말"
            />
          </h2>
          <span className="hint">
            {(tokens.data?.items.length ?? 0)}개 발급됨 · 펼치기
          </span>
        </summary>

        {tokens.error ? <p className="hint p-4">{tokens.error}</p> : null}
        {(tokens.data?.items.length ?? 0) === 0 ? (
          <p className="hint p-4">발급된 토큰이 없습니다.</p>
        ) : (
          <div className="table-scroll mt-3">
            <table className="table min-w-[40rem]">
              <thead>
                <tr>
                  <th>이름</th>
                  <th>발급</th>
                  <th>만료</th>
                  <th className="col-num">사용</th>
                  <th>마지막 사용</th>
                  <th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {tokens.data?.items.map((row) => (
                  <tr key={row.id}>
                    <td className="cell-strong">
                      {row.label || '(이름 없음)'}
                      {row.expired ? <Badge tone="neutral">만료</Badge> : null}
                    </td>
                    <td className="cell-muted"><RelTime iso={row.created_at} /></td>
                    <td className="cell-muted"><RelTime iso={row.expires_at} /></td>
                    <td className="col-num">{row.use_count}</td>
                    <td className="cell-muted">
                      {row.last_used_at ? `${relativeTime(row.last_used_at)} · ${row.last_used_ip}` : '—'}
                    </td>
                    <td className="col-actions">
                      <IconButton
                        icon={Trash2}
                        label="폐기"
                        subject={row.label || '설치 토큰'}
                        danger
                        disabled={action.pending}
                        onClick={() => void action.run(() => client.revokeInstallToken(row.id), tokens.reload)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
      ) : null}

      {uploading ? (
        <UploadReleaseModal
          client={client}
          onClose={() => setUploading(false)}
          onUploaded={(version) => {
            setUploading(false);
            setUploaded(version);
            catalog.reload();
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────── 롤아웃 ───────────────────────────────

/**
 * 롤아웃 판.
 *
 * **카나리를 먼저 쓰도록 유도한다.** 두 채널의 버튼을 똑같이 두면 사람은 더 쉬운 쪽,
 * 즉 전 화면을 한 번에 바꾸는 쪽을 누른다. 그래서 카나리는 한 번에 지정되고, 안정은
 * 영향 범위와 검증 여부를 보여주는 확인 단계를 한 겹 둔다.
 */
function RolloutPanel({
  client,
  releases,
  state,
  error,
  stableVersion,
  canaryVersion,
  uploaded,
  onUpload,
  onChanged,
}: {
  client: SignageOneClient;
  releases: AgentRelease[];
  /** 채널별 화면 수까지 담긴 현황. `admin` 이 아니면 null 이다. */
  state: RolloutState | null;
  error: string | null;
  stableVersion: string | null;
  canaryVersion: string | null;
  /** 방금 올린 버전. 아직 어느 채널에도 없다면 그 사실을 말한다. */
  uploaded: string | null;
  /** 산출물 등록. 배포의 재료를 여기서 들인다. */
  onUpload: () => void;
  onChanged: () => void;
}): ReactNode {
  const action = useAction();
  const options = releaseVersions(releases);
  const [canaryPick, setCanaryPick] = useState('');
  const [stablePick, setStablePick] = useState('');
  const [confirming, setConfirming] = useState<VersionOption | null>(null);

  const apply = (input: { stable_version?: string | null; canary_version?: string | null; install_window?: InstallWindow | null }): void => {
    void action.run(() => client.setRollout(input), () => {
      setCanaryPick('');
      setStablePick('');
      setConfirming(null);
      onChanged();
    });
  };

  const pendingUpload = uploaded && uploaded !== stableVersion && uploaded !== canaryVersion ? uploaded : null;

  return (
    <Panel
      title="롤아웃"
      help="채널에 지정한 버전만 화면에 배포됩니다. 카나리에서 먼저 확인한 뒤 안정 채널로 승격하세요."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          {state?.updated_at ? (
            <span className="hint">마지막 변경 <RelTime iso={state.updated_at} /></span>
          ) : null}
          {/*
            산출물 등록은 배포의 재료다. 내려받기 판을 걷어내면서 이리로 옮겼다 —
            올릴 곳이 없으면 채널에 지정할 버전 자체가 생기지 않는다.
          */}
          <button type="button" className="btn" onClick={onUpload}>
            <Upload className="h-3.5 w-3.5" strokeWidth={2} />
            산출물 올리기
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        {pendingUpload ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            <strong className="font-medium">v{pendingUpload} 를 등록했습니다.</strong> 아직 배포되지
            않았습니다. 카나리로 먼저 지정하세요.
          </p>
        ) : null}

        {/*
          **승격이 실제로 퍼졌는가.**

          승격은 화면이 물어봐야 일어난다. 물지 않는 화면은 아무 소리도 내지 않아서,
          올린 사람은 그것이 닿았는지 알 길이 없었다 — 현장의 한 대가 네 버전을 건너뛴 채
          돌고 있었고 서버 로그를 뒤져서야 알았다. 승격 단추 옆이 그것을 말할 자리다.
        */}
        {state?.behind ? (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[12.5px] text-rose-800">
            <strong className="font-medium">{state.behind}대가 지정 버전보다 낮습니다.</strong>
            <HelpTip
              text="승격은 화면이 업데이트를 확인해야 적용됩니다. 계속 낮게 남는 화면은 업데이트를 확인하지 못하는 상태일 수 있습니다."
              label="버전 미달 도움말"
            />{' '}
            <a className="link" href="#/displays">화면 목록</a> 의 버전 열에서 확인한 뒤, 현장에서 정비 모드 ▸
            [지금 확인] ▸ [지금 업데이트] 를 누르세요.
          </p>
        ) : null}

        {options.length === 0 ? (
          <Empty
            title="등록된 버전이 없습니다"
            hint="산출물을 올리면 채널에 지정할 수 있습니다."
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ChannelCard
              icon={FlaskConical}
              tone="canary"
              title="카나리"
              detail="채널을 카나리로 둔 화면에 먼저 배포합니다."
              version={canaryVersion}
              displays={state?.displays.canary ?? null}
            >
              <div className="flex flex-wrap items-center gap-2">
                {/*
                  좁은 화면에서는 고르기 칸이 줄어들게 둔다. 선택지 글자(버전 · 플랫폼 목록)가
                  길어 기본 최소 폭으로는 카드 밖으로 밀려난다. 넓은 화면은 원래대로 둔다.
                */}
                <select
                  className="input min-w-0 flex-1 lg:min-w-[auto]"
                  value={canaryPick}
                  disabled={action.pending}
                  aria-label="카나리로 지정할 버전"
                  onChange={(event) => setCanaryPick(event.target.value)}
                >
                  <option value="">버전 선택…</option>
                  {options.map((option) => (
                    <option key={option.version} value={option.version}>
                      v{option.version} · {option.targets.join(' · ')}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn"
                  disabled={action.pending || !canaryPick || canaryPick === canaryVersion}
                  onClick={() => apply({ canary_version: canaryPick })}
                >
                  카나리로 지정
                </button>
              </div>
              {canaryVersion ? (
                <button
                  type="button"
                  className="link mt-2 w-fit"
                  disabled={action.pending}
                  onClick={() => apply({ canary_version: null })}
                >
                  카나리 해제
                </button>
              ) : null}
            </ChannelCard>

            <ChannelCard
              icon={Rocket}
              tone="stable"
              title="안정 (stable)"
              detail="채널을 따로 두지 않은 모든 화면이 대상입니다. 되돌릴 수 없습니다."
              version={stableVersion}
              displays={state?.displays.stable ?? null}
            >
              <div className="flex flex-wrap items-center gap-2">
                {/*
                  안정 승격은 select 에서 바로 나가지 않는다. 고른 다음 한 번 더 —
                  대상 화면 수와 카나리 검증 여부를 보고 누르게 한다.
                */}
                <select
                  className="input min-w-0 flex-1 lg:min-w-[auto]"
                  value={stablePick}
                  disabled={action.pending}
                  aria-label="안정 채널로 승격할 버전"
                  onChange={(event) => setStablePick(event.target.value)}
                >
                  <option value="">버전 선택…</option>
                  {options.map((option) => (
                    <option key={option.version} value={option.version} disabled={option.version === stableVersion}>
                      v{option.version} · {option.targets.join(' · ')}
                      {option.version === canaryVersion ? ' (카나리 검증 중)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={action.pending || !stablePick}
                  onClick={() =>
                    setConfirming(options.find((option) => option.version === stablePick) ?? null)
                  }
                >
                  승격…
                </button>
              </div>
              {stableVersion ? (
                <button
                  type="button"
                  className="link link-danger mt-2 w-fit"
                  disabled={action.pending}
                  onClick={async () => {
                    const ok = await confirmDialog({
                      title: '안정 채널 배포를 중단할까요?',
                      body: '이미 업데이트된 화면은 그대로 유지됩니다.',
                      confirmLabel: '배포 중단',
                      danger: true,
                    });
                    if (!ok) return;
                    apply({ stable_version: null });
                  }}
                >
                  배포 중단
                </button>
              ) : null}
            </ChannelCard>
          </div>
        )}

        {/* 옛 서버는 시간대를 모른다(undefined) — 그때는 줄을 그리지 않는다. */}
        {state && state.install_window !== undefined ? (
          <InstallWindowRow
            current={state.install_window}
            pending={action.pending}
            onSave={(install_window) => apply({ install_window })}
          />
        ) : null}

        {/*
          핀은 채널을 이긴다. 이 수를 감추면 "왜 이 화면들만 안 올라가지"를 헤맨다.
        */}
        {/* 고정된 화면이 없으면 말하지 않는다 — 0대라는 안내는 읽을 일만 늘린다. */}
        {state && state.displays.pinned > 0 ? (
          <p className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[12.5px] text-slate-600">
            버전이 고정된 화면 <strong className="font-medium text-slate-800">{state.displays.pinned}대</strong>는
            롤아웃에서 제외됩니다. 고정은 화면 상세 ▸{' '}
            <strong className="font-medium text-slate-800">소프트웨어</strong>에서 해제합니다.
          </p>
        ) : null}

        {error ? <p className="hint">채널별 화면 수를 불러오지 못했습니다: {error}</p> : null}
      </div>

      {confirming ? (
        <PromoteModal
          option={confirming}
          stableVersion={stableVersion}
          canaryVersion={canaryVersion}
          displays={state?.displays.stable ?? null}
          pending={action.pending}
          onClose={() => setConfirming(null)}
          onConfirm={() => apply({ stable_version: confirming.version })}
        />
      ) : null}
    </Panel>
  );
}

/** 설치 시간대를 사람 말로 — "02:00–05:00", 자정을 넘으면 "23:00–다음 날 05:00". 없으면 "언제든". */
export function installWindowText(window: InstallWindow | null | undefined): string {
  if (!window) return '언제든';
  return window.end <= window.start && window.end !== '24:00' ? `${window.start}–다음 날 ${window.end}` : `${window.start}–${window.end}`;
}

/** 입력한 시간대의 문제 — 없으면 null. 서버도 같은 것을 막는다(`installWindowSchema`). */
export function installWindowProblem(start: string, end: string): string | null {
  const pattern = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!pattern.test(start) || !pattern.test(end)) return '시작과 끝을 모두 고르세요';
  if (start === end) return '시작과 끝이 같습니다 — 언제든 깔게 하려면 [언제든] 을 누르세요';
  return null;
}

/**
 * 무인 업데이트 설치 시간대(2026-10-19 기능 점검 G14).
 *
 * 예전에는 승격하는 순간 모든 화면이 곧바로 받아 재시작했다 — 낮에 올리면 영업 중인 매장의 화면이 깜빡였다. 승격과 같은
 * 자리에 두는 이유는 **승격을 누르는 사람이 언제 깔릴지를 알아야** 하기 때문이다.
 */
function InstallWindowRow({
  current,
  pending,
  onSave,
}: {
  current: InstallWindow | null;
  pending: boolean;
  onSave: (window: InstallWindow | null) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(current?.start ?? '02:00');
  const [end, setEnd] = useState(current?.end ?? '05:00');
  const problem = installWindowProblem(start, end);

  const open = (): void => {
    setStart(current?.start ?? '02:00');
    setEnd(current?.end ?? '05:00');
    setEditing(true);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-800">
          <Clock className="h-4 w-4 text-slate-400" strokeWidth={2} />
          설치 시간대
          <HelpTip
            text="무인 업데이트를 이 시간대에만 깝니다(화면이 선 곳의 시각). 승격해도 시간대가 올 때까지 기다렸다가 깔고, 화면마다 시작을 조금씩(최대 30분) 늦춰 한꺼번에 받지 않게 합니다. 현장 정비 화면의 [지금 업데이트] 는 시간대와 상관없이 깝니다. 에이전트 0.7.17 부터 따릅니다."
            label="설치 시간대 도움말"
          />
        </span>
        {!editing ? (
          <button type="button" className="btn btn-tiny" disabled={pending} onClick={open}>
            바꾸기…
          </button>
        ) : null}
      </div>
      {!editing ? (
        <p className="mt-2 text-[13px] text-slate-700">
          <strong className="font-medium">{installWindowText(current)}</strong>
          <span className="cell-muted"> {current ? '· 화면 현지 시각' : '· 승격하면 곧바로 깝니다'}</span>
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="time"
              className="input w-[8.5rem]"
              value={start}
              aria-label="설치 시작 시각"
              onChange={(event) => setStart(event.target.value)}
            />
            <span className="text-slate-500">–</span>
            <input
              type="time"
              className="input w-[8.5rem]"
              value={end}
              aria-label="설치 끝 시각"
              onChange={(event) => setEnd(event.target.value)}
            />
            {!problem && end < start ? <span className="hint">자정을 넘어 다음 날 {end} 까지</span> : null}
          </div>
          {problem ? <p className="hint text-amber-700">{problem}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <button
              key="save"
              type="button"
              className="btn btn-primary"
              disabled={pending || problem !== null}
              onClick={() => {
                onSave({ start, end });
                setEditing(false);
              }}
            >
              저장
            </button>
            <button
              type="button"
              className="btn"
              disabled={pending || current === null}
              onClick={() => {
                onSave(null);
                setEditing(false);
              }}
            >
              언제든
            </button>
            <button key="cancel" type="button" className="btn" disabled={pending} onClick={() => setEditing(false)}>
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChannelCard({
  icon: Icon,
  tone,
  title,
  detail,
  version,
  displays,
  children,
}: {
  icon: typeof Monitor;
  tone: 'stable' | 'canary';
  title: string;
  detail: string;
  version: string | null;
  /** 대상 화면 수. 권한이 없어 모르면 null — 0 과 구분해야 한다. */
  displays: number | null;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-800">
          <Icon className="h-4 w-4 text-slate-400" strokeWidth={2} />
          {title}
        </span>
        {displays !== null ? (
          <Badge tone={tone === 'stable' ? 'brand' : 'warn'}>화면 {displays}대</Badge>
        ) : null}
      </div>

      <p className="mt-2 font-mono text-[18px] text-slate-800">{version ? `v${version}` : '지정 안 함'}</p>
      <p className="mt-1 text-[12.5px] leading-snug text-slate-500">{detail}</p>

      <div className="mt-3 flex flex-col">{children}</div>
    </div>
  );
}

/**
 * 안정 승격 확인.
 *
 * 여기서 보여줘야 하는 것은 "정말 하시겠습니까"가 아니라 **결정에 필요한 사실**이다:
 * 몇 대가 대상인지, 카나리에서 검증했는지, 어떤 플랫폼이 빠졌는지. 롤백이 없기 때문에
 * 누른 뒤에는 이 정보를 볼 기회가 없다.
 */
function PromoteModal({
  option,
  stableVersion,
  canaryVersion,
  displays,
  pending,
  onClose,
  onConfirm,
}: {
  option: VersionOption;
  stableVersion: string | null;
  canaryVersion: string | null;
  displays: number | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactNode {
  const verified = option.version === canaryVersion;
  const downgrade = stableVersion !== null && compareVersions(option.version, stableVersion) < 0;
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Modal title="안정 채널로 승격" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <dl className="kv">
          <dt>버전</dt>
          <dd className="mono">
            {stableVersion ? `v${stableVersion} → ` : ''}
            <strong>v{option.version}</strong>
          </dd>
          <dt>대상 화면</dt>
          <dd>
            {displays === null ? '—' : `${displays}대`}
            <span className="hint hint-inline">고정 화면 제외</span>
          </dd>
          {/* 빠진 플랫폼은 조용히 업데이트를 못 받는다 — 나중에 원인을 찾기 어려운 종류다. */}
          <dt>
            산출물
            <HelpTip
              text="이 버전이 등록되지 않은 플랫폼의 화면은 업데이트되지 않고 현재 버전에 머뭅니다."
              label="산출물 도움말"
            />
          </dt>
          <dd>
            {option.targets.join(' · ')} · {option.count}개
          </dd>
          <dt>자동 설치</dt>
          <dd>{option.autoUpdate.length > 0 ? option.autoUpdate.join(' · ') : '없음'}</dd>
        </dl>

        {/*
          산출물이 있어도 서명이 없으면 화면은 받아 놓고 설치를 거부한다. 승격 전에
          말하지 않으면 그 사실을 현장에서 발견하게 된다.
        */}
        {option.targets.some((target) => !option.autoUpdate.includes(target)) ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            <strong className="font-medium">
              {option.targets.filter((target) => !option.autoUpdate.includes(target)).join(' · ')}
            </strong>{' '}
            는 업데이트용 산출물·서명이 없어 자동 설치되지 않습니다. 해당 화면은 직접 다시 설치해야
            합니다.
          </p>
        ) : null}

        {downgrade ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">
            <strong className="font-medium">현재보다 낮은 버전입니다.</strong> 데스크톱 화면은
            다운그레이드되지 않습니다. 되돌리려면 수정한 상위 버전을 올리세요.
          </p>
        ) : null}

        {verified ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-800">
            카나리 채널에 배포 중인 버전입니다.
          </p>
        ) : (
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>
              <strong className="font-medium">카나리에서 검증되지 않은 버전입니다.</strong> 되돌릴 수
              없음을 확인했습니다.
            </span>
          </label>
        )}

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={pending}>
            취소
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending || (!verified && !acknowledged)}
            onClick={onConfirm}
          >
            {pending ? '적용 중…' : `${displays === null ? '' : `${displays}대에 `}승격`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────── 조각 ───────────────────────────────

/**
 * 안드로이드 설치.
 *
 * 스크립트가 없다. 안드로이드에는 붙여 넣을 터미널이 없고, TV 박스에는 대개
 * 브라우저조차 없다. 그래서 **기기 종류에 따라 길이 셋**이고, 실제로 쓰이는
 * 순서대로 놓았다.
 *
 * 토큰을 주소에 싣는 것은 이 경로에서만 하는 예외다 — 다운로더 앱은 헤더를
 * 붙이지 못한다. 대신 수명이 짧고 할 수 있는 일이 내려받기뿐이다.
 */
/**
 * 안드로이드 기본 아키텍처.
 *
 * 등록된 것 중 `arm64-v8a` 를 먼저 고른다 — 기기 대부분이 그것이다. 목록 순서
 * (최근 등록 순)를 그대로 쓰면 나중에 올린 32비트 빌드가 기본이 되고, 스캔한
 * 사람이 자기 기기에 안 맞는 APK 를 받는다. 실제로 그렇게 짰다가 잡았다.
 */
/** 서버가 `arch` 없이 받은 요청에 쓰는 값. 같은 값이면 주소에서 뺀다 — 칠 글자가 줄어든다. */
export const DEFAULT_APK_ARCH = 'arm64-v8a';

export function defaultAndroidArch(releases: readonly { arch: string }[]): string {
  if (releases.some((release) => release.arch === DEFAULT_APK_ARCH)) return DEFAULT_APK_ARCH;
  return releases[0]?.arch ?? DEFAULT_APK_ARCH;
}

/**
 * 고를 수 있는 아키텍처 — **산출물 하나당 하나가 아니라 종류당 하나다.**
 *
 * 버전은 쌓인다. 목록을 그대로 단추로 펴면 arm64 빌드를 여섯 번 올린 테넌트에서
 * `arm64-v8a` 단추가 여섯 개 서고, 전부 눌린 것처럼 보인다(같은 값이라 `aria-pressed`
 * 가 모두 참이다). 고를 것은 처음부터 "칩이 무엇이냐" 하나뿐이었다.
 *
 * 순서는 받은 순서를 따른다 — 목록이 최신순이라 지금 내보내는 빌드의 아키텍처가 앞에 선다.
 */
export function androidArchs(releases: readonly { arch: string }[]): string[] {
  return [...new Set(releases.map((release) => release.arch))];
}

/**
 * 지금 보여줄 아키텍처.
 *
 * **고른 값을 상태에 담아 두기만 하면 안 된다.** 이 화면은 APK 가 하나도 없는 상태로
 * 먼저 뜨고(카탈로그 로딩·업로드 전), 그때 정해진 기본값은 `arm64-v8a` 다. 그 뒤에
 * `armeabi-v7a` 만 올리면 고른 값은 등록되지 않은 것을 계속 가리키고, **QR 은 영영
 * 나타나지 않는다** — 방금 올렸는데도.
 *
 * 그래서 고른 값이 실제 등록 목록에 없으면 조용히 버린다. 사람이 고른 것은 존중하되,
 * 존재하지 않는 선택을 붙들고 있지는 않는다.
 */
export function resolveArch(chosen: string | null, releases: readonly { arch: string }[]): string {
  if (chosen && releases.some((release) => release.arch === chosen)) return chosen;
  return defaultAndroidArch(releases);
}

function AndroidInstall({
  baseUrl,
  releases,
}: {
  baseUrl: string;
  releases: AgentRelease[];
}): ReactNode {
  /*
   * 기본 아키텍처는 **실제로 등록된 것**을 따른다. 기기 대부분이 arm64-v8a 이지만,
   * armeabi-v7a 만 올려 둔 상태에서 arm64 주소를 QR 로 내주면 스캔한 사람이 404 를
   * 만난다. 화면이 없는 것을 있는 척하지 않게 한다.
   *
   * 고른 값은 상태에 담되 **매 렌더에서 등록 목록과 맞춰 본다.** 목록은 나중에 도착하고
   * (업로드 직후 새로고침) 그때 고른 값이 유효하지 않을 수 있다.
   */
  const [chosen, setChosen] = useState<string | null>(null);
  const arch = resolveArch(chosen, releases);
  const picked = releases.find((release) => release.arch === arch) ?? null;
  const ready = Boolean(picked);
  /*
   * **주소에 토큰을 싣지 않는다(2026-09-22).**
   *
   * 현장에서 이 주소를 여는 사람은 리모컨이나 가상 키보드를 쥐고 있다. 그 손으로 긴 토큰이나
   * 설치 코드를 넣는 것이 설치가 엎어지는 주된 이유였다. 이제 서버가 토큰 없이도 안내 화면과
   * 파일을 주므로(`/apk`), 콘솔이 내미는 주소도 **칠 수 있는 길이**여야 한다.
   *
   * 토큰 경로가 없어진 것은 아니다 — 윈도우·리눅스 설치 스크립트는 그대로 토큰을 쓰고,
   * 토큰·코드로 들어온 내려받기는 사용 이력이 남는다.
   */
  const apkUrl = `${baseUrl}/apk${arch === DEFAULT_APK_ARCH ? '' : `?arch=${arch}`}`;
  const host = baseUrl.replace(/^https?:\/\//, '');

  return (
    <div className="flex flex-col gap-4">
      {/* 아직 올린 APK 가 없으면 그 사실을 먼저 말한다 — 열리지 않는 QR 을 내주지 않는다. */}
      {releases.length === 0 ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
          <strong className="font-medium">등록된 APK 가 없습니다.</strong>{' '}
          <strong className="font-medium">산출물 올리기</strong>로 APK 를 등록하면 아래 경로가 동작합니다.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-slate-500">
            아키텍처
            <HelpTip
              text="대부분의 기기는 arm64-v8a 입니다. 구형 32비트 기기는 armeabi-v7a 를 고르세요(해당 빌드를 등록해야 표시됩니다)."
              label="아키텍처 도움말"
            />
          </span>
          <div className="seg seg-sm">
            {androidArchs(releases).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={arch === value}
                onClick={() => setChosen(value)}
                className="seg-btn mono"
              >
                {value}
              </button>
            ))}
          </div>
          {picked ? (
            <span className="text-slate-400">
              v{picked.version} · {formatBytes(picked.size)} · <RelTime iso={picked.published_at} />
            </span>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-slate-200 p-3">
          {/*
            QR 은 조건이 둘이다 — 등록된 APK 와 **이 화면에서 발급한 설치 토큰**.
            토큰 평문은 발급 순간에만 존재하므로(해시만 저장한다) 이미 발급된 것을
            다시 꺼내 쓸 수 없고, 그래서 열 때마다 한 번은 눌러야 한다.

            그 버튼이 QR 에서 멀리 떨어져 있으면 사람은 "QR 이 안 나온다" 로 읽는다.
            **고칠 수단을 빈 자리 안에 둔다.**
          */}
          {/*
            **QR 에 토큰을 싣지 않으므로 발급 단계가 사라졌다.** 예전에는 이 자리가 비어 있고
            "토큰을 발급해야 QR 이 나온다" 는 안내가 섰는데, 현장에서는 그 한 단계가 곧
            "QR 이 안 나온다" 로 읽혔다. 이제 등록된 APK 만 있으면 바로 그린다.
          */}
          {ready ? (
            <QrCode value={apkUrl} label="APK 내려받기 QR 코드" size={148} />
          ) : (
            <div className="flex h-[148px] w-[148px] items-center justify-center rounded-lg border border-dashed border-slate-200 px-3 text-center text-[11.5px] leading-snug text-slate-400">
              APK 를 등록하면 QR 이 나타납니다
            </div>
          )}
          <span className="text-[11.5px] text-slate-400">스캔 → 설명 화면 → [내려받기]</span>
        </div>

        {/* 길 목록과 [다른 설치 방법] 을 한 칸에 — 격자의 셋째 칸으로 떨어지지 않게. */}
        <div className="flex min-w-0 flex-col gap-3">
          <ol className="flex flex-col gap-2.5">
            <Route
              n={1}
              title="태블릿 · 폰 · 카메라가 있는 기기"
              detail="QR 을 스캔해 열린 화면에서 [설치 파일 내려받기] 를 누릅니다. 토큰·코드는 필요 없습니다."
            />
            {/*
              설치 코드 — 옛 브라우저로 들어오는 사이니지 기기용.

              크롬 72 같은 기기 브라우저는 이 콘솔을 읽지 못해 빈 화면이었고, 긴 토큰 주소를
              리모컨으로 칠 수는 없었다. 도메인만 쳐도 콘솔(index.html)이 코드 입력 화면으로
              보내므로, 여기서는 **짧은 코드 하나만** 크게 보여 준다.
            */}
            {/*
              **리모컨으로 칠 수 있는 길이가 전부다.** 예전에는 이 자리에서 설치 코드 8자를
              더 넣어야 했는데, 리모컨 가상 키보드로 그것을 치다 실패하는 것이 설치가 엎어지는
              주된 이유였다. 이제 주소 하나면 설명 화면이 열린다.
            */}
            <Route
              n={2}
              title="TV 박스 · 스틱 (브라우저가 있을 때)"
              detail="기기 브라우저 주소창에 아래 주소를 입력합니다. 옛 브라우저(크롬 72 등)는 도메인만 입력해도 됩니다."
            >
              <p className="font-mono text-[18px] font-semibold tracking-wide text-slate-900">{host}/apk</p>
            </Route>
          </ol>
          {/*
            대개는 1·2 로 끝난다. 다운로더 앱·adb 는 그 둘이 막혔을 때 쓰는 길이라 [다른 설치 방법] 뒤에 둔다(점진적 공개).
          */}
          <Disclosure title="다른 설치 방법" summary="다운로더 앱 · PC 에서 adb">
            <ol className="flex flex-col gap-2.5">
              <Route
                n={3}
                title="다운로더 앱 (주소창만 있을 때)"
                detail="아래 주소를 입력하면 파일을 바로 내려받습니다. 리모컨 입력이 어려우면 원격 입력 앱을 쓰세요."
              >
                <CommandBox command={`${apkUrl}${apkUrl.includes('?') ? '&' : '?'}dl=1`} />
              </Route>
              <Route
                n={4}
                title="TV 박스 · 스틱 (가장 확실한 방법)"
                detail="같은 네트워크의 PC 에서 adb 로 설치합니다. 기기의 개발자 옵션에서 USB/네트워크 디버깅을 먼저 켜세요."
              >
                <CommandBox command={`curl -fsSL -o agent.apk "${apkUrl}"\nadb connect <기기IP>:5555\nadb install -r agent.apk`} />
              </Route>
            </ol>
          </Disclosure>
        </div>
      </div>

      {/*
        "앱이 설치되지 않았습니다".

        안드로이드가 설치를 거부할 때 내놓는 **유일한 문장**이다. 이유를 화면에 남기지
        않아 현장에서는 손쓸 데가 없다. 실제로 겪은 순서대로 적어 둔다 — 이게 없으면
        같은 자리에서 매번 처음부터 헤맨다.
      */}
      <details className="rounded-lg border border-slate-200 px-3.5 py-3 text-[12.5px] text-slate-600">
        <summary className="cursor-pointer font-medium text-slate-800">
          “앱이 설치되지 않았습니다” 가 뜰 때
        </summary>
        <ol className="mt-2 flex list-decimal flex-col gap-1.5 pl-4">
          <li>
            <strong className="font-medium text-slate-800">기존 앱을 삭제하고 다시 설치합니다.</strong>{' '}
            <span className="font-mono">다른 키로 서명된</span> 같은 앱이 있으면 덮어쓸 수 없습니다(가장 흔한
            원인). 설정 ▸ 앱 ▸ SignageOne Player ▸ 삭제.
          </li>
          <li>
            <strong className="font-medium text-slate-800">안드로이드 5.1 이상</strong>인지 확인합니다. 그
            이하는 설치되지 않습니다.
          </li>
          <li>
            <strong className="font-medium text-slate-800">Play 프로텍트</strong>가 막을 수 있습니다. Play
            스토어 ▸ 프로필 ▸ Play 프로텍트 ▸ 스캔 끄기 후 다시 시도합니다.
          </li>
          <li>
            그래도 안 되면 위 [다른 설치 방법] 의 <strong className="font-medium text-slate-800">adb 명령</strong>으로 설치해{' '}
            <span className="font-mono">INSTALL_FAILED_…</span> 원인을 확인합니다.
          </li>
        </ol>
      </details>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-[12.5px] text-amber-800">
        <p className="font-medium">설치 전 확인</p>
        <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4">
          <li>
            <strong>알 수 없는 출처</strong>를 허용합니다. 안드로이드 8 이상은 내려받은 앱(브라우저·다운로더)별로,
            5.1~7 은 설정 ▸ 보안에서 켭니다.
          </li>
          <li>기기의 화면 꺼짐·절전을 끄고 자동 실행을 켭니다.</li>
        </ul>
      </div>
    </div>
  );
}

function Route({
  n,
  title,
  detail,
  children,
}: {
  n: number;
  title: string;
  detail: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <li className="rounded-lg border border-slate-200 px-3.5 py-3">
      <p className="text-[13.5px] font-medium text-slate-800">
        <span className="mr-1.5 font-mono text-slate-400">{n}</span>
        {title}
      </p>
      <p className="mt-0.5 text-[12.5px] leading-snug text-slate-500">{detail}</p>
      {children ? <div className="mt-2">{children}</div> : null}
    </li>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Monitor;
  children: ReactNode;
}): ReactNode {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className="seg-btn">
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      {children}
    </button>
  );
}

function UploadReleaseModal({
  client,
  onClose,
  onUploaded,
}: {
  client: SignageOneClient;
  onClose: () => void;
  /** 올린 버전을 넘긴다 — 화면이 "이제 롤아웃에서 지정해야 나갑니다"를 말할 수 있게. */
  onUploaded: (version: string) => void;
}): ReactNode {
  const [version, setVersion] = useState('');
  const [target, setTarget] = useState<AgentRelease['target']>('darwin');
  const [arch, setArch] = useState<AgentRelease['arch']>('aarch64');
  const [notes, setNotes] = useState('');
  const [signature, setSignature] = useState('');
  const [kind, setKind] = useState<AgentRelease['kind']>('installer');
  const [file, setFile] = useState<File | null>(null);
  const action = useAction();

  /*
   * 아키텍처 이름은 플랫폼마다 다르다.
   *
   * 안드로이드는 ABI 이름(`arm64-v8a`)을 쓰고 데스크톱은 `aarch64` 를 쓴다. 사실상
   * 같은 칩이지만 APK 를 만들 때 쓰는 이름이 그것이고, 화면이 조회할 때 보내는 값도
   * 그것이다. 섞어 올리면 그 화면은 자기 산출물을 영영 찾지 못한다.
   */
  const arches: Array<{ value: AgentRelease['arch']; label: string }> =
    target === 'android'
      ? [
          { value: 'arm64-v8a', label: 'arm64-v8a (TV 박스·태블릿 대부분)' },
          { value: 'armeabi-v7a', label: 'armeabi-v7a (구형 32비트)' },
        ]
      : [
          { value: 'aarch64', label: 'aarch64 (Apple Silicon · ARM)' },
          { value: 'x86_64', label: 'x86_64 (Intel · AMD)' },
        ];

  return (
    <Modal title="산출물 올리기" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!file) return;
          const trimmed = version.trim();
          const form = new FormData();
          form.set('version', trimmed);
          form.set('target', target);
          form.set('arch', arch);
          form.set('notes', notes.trim());
          form.set('signature', signature.trim());
          form.set('kind', kind);
          form.set('file', file);

          void action.run(async () => {
            const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
            // 업로드는 multipart 라 JSON 경로를 쓰지 않는다.
            const response = await fetch(client.mediaUrl('/api/v1/agent/releases/upload'), {
              method: 'POST',
              body: form,
              headers: token ? { authorization: `Bearer ${token}` } : {},
              credentials: 'include',
            });
            const body = (await response.json().catch(() => null)) as { message?: string } | null;
            if (!response.ok) throw new Error(body?.message ?? `업로드 실패 (${response.status})`);
          }, () => onUploaded(trimmed));
        }}
      >
        <label className="field">
          <span className="field-label">
            버전
            <HelpTip text="x.y.z 형식입니다. 같은 버전·플랫폼으로 다시 올리면 덮어씁니다." label="버전 도움말" />
          </span>
          <input
            className="input"
            required
            placeholder="0.1.0"
            pattern="\d+\.\d+\.\d+"
            value={version}
            disabled={action.pending}
            onChange={(event) => setVersion(event.target.value)}
          />
        </label>

        <div className="grid-2">
          <label className="field">
            <span className="field-label">플랫폼</span>
            <select
              className="input"
              value={target}
              disabled={action.pending}
              onChange={(event) => {
                const next = event.target.value as AgentRelease['target'];
                setTarget(next);
                // 플랫폼을 바꾸면 아키텍처도 그 플랫폼의 이름으로 옮긴다.
                setArch(next === 'android' ? 'arm64-v8a' : 'aarch64');
              }}
            >
              <option value="darwin">macOS</option>
              <option value="windows">Windows</option>
              <option value="linux">Linux</option>
              <option value="android">Android</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">아키텍처</span>
            <select
              className="input"
              value={arch}
              disabled={action.pending}
              onChange={(event) => setArch(event.target.value as AgentRelease['arch'])}
            >
              {arches.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field-label">설치 파일</span>
          <input
            className="input"
            type="file"
            required
            accept=".dmg,.msi,.exe,.AppImage,.deb,.apk,.zip,.gz"
            disabled={action.pending}
            onChange={(event) => {
              const picked = event.target.files?.[0] ?? null;
              setFile(picked);
              // 압축본이면 업데이트용이다. 사람이 매번 고르게 하면 언젠가 틀린다.
              if (picked) setKind(guessKind(picked.name));
            }}
          />
        </label>


        {/*
          서명이 없으면 처음 설치는 되지만 **자동 업데이트는 되지 않는다.** 데스크톱
          업데이터가 서명을 확인한 뒤에만 설치하기 때문이다. 여기서 말해 두지 않으면
          "올렸는데 화면이 안 올라간다"의 원인이 어디에도 드러나지 않는다.
        */}
        {target !== 'android' && kind === 'update' ? (
          <label className="field">
            <span className="field-label">
              업데이터 서명
              <HelpTip
                text="빌드 산출물 옆에 있는 같은 이름의 .sig 파일 내용입니다. 데스크톱 업데이터는 서명을 확인한 뒤에만 설치합니다."
                label="업데이터 서명 도움말"
              />
            </span>
            <textarea
              className="input"
              rows={3}
              value={signature}
              disabled={action.pending}
              placeholder=".sig 파일 내용을 붙여 넣습니다"
              onChange={(event) => setSignature(event.target.value)}
            />
            <small className="hint">
              비워 두면 화면이 <strong>설치를 거부합니다</strong>.
            </small>
          </label>
        ) : null}

        {/*
          용도는 파일을 고르면 확장자로 정해지고 메모는 비워 둬도 된다 — [추가 옵션] 뒤로(점진적 공개).
          접힌 머리에 정해진 용도를 적어, 틀렸으면 열어서 고친다.
        */}
        <Disclosure title="추가 옵션" summary={`${kind === 'update' ? '자동 업데이트용' : '처음 설치용'}${notes ? ' · 메모 있음' : ''}`}>
          <div className="space-y-3">
            <label className="field">
              <span className="field-label">
                용도
                <HelpTip
                  text="파일을 고르면 확장자로 정해집니다. 데스크톱은 처음 설치용(.dmg·.msi 등)과 자동 업데이트용(.tar.gz·.zip)을 모두 올리고, 안드로이드는 APK 하나면 됩니다."
                  label="용도 도움말"
                />
              </span>
              <select
                className="input"
                value={kind}
                disabled={action.pending}
                onChange={(event) => setKind(event.target.value as AgentRelease['kind'])}
              >
                <option value="installer">처음 설치용</option>
                <option value="update">자동 업데이트용</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">메모</span>
              <input
                className="input"
                maxLength={200}
                value={notes}
                disabled={action.pending}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>
          </div>
        </Disclosure>

        {/*
          올리는 사람 대부분은 이걸 배포라고 생각한다. 올린 다음에 말하면 늦고,
          "왜 안 나가지"로 한 번 헤맨 뒤에야 롤아웃을 찾는다. 누르기 전에 말한다.
        */}
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
          올리기만으로는 <strong className="font-medium">배포되지 않습니다.</strong> 등록 후{' '}
          <strong className="font-medium">롤아웃</strong>에서 채널에 지정하세요.
        </p>

        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending || !file}>
            {action.pending ? '올리는 중…' : '올리기'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
